import Phaser from 'phaser';
import { COLORS, FONTS, GAME_HEIGHT, GAME_WIDTH, fontStr } from '@/config';
import { gameState } from '@/state/store';
import type { ContractedBook } from '@/data/types';
import {
  UTENLANDSKE_FORLAG,
  type UtenlandskForlag,
} from '@/data/utenlandske';
import {
  UTENLANDS_MIN_SALG_GLOBAL,
  UTENLANDS_MIN_SNITT_GLOBAL,
  beregnUtenlandsTilbud,
  snittTerninger,
} from '@/systems/utenlandsSalg';
import { animateModalGlide } from '@/ui/modalGlide';

const SHEET_WIDTH = 1000;
const ROW_WIDTH = 940;
const ROW_PADDING = 24;
const BOK_RAD_HOYDE = 160;
const BOK_RAD_GAP = 16;
const FORLAG_RAD_HOYDE = 180;
const FORLAG_RAD_GAP = 16;

type Fase = 'velgBok' | 'velgForlag' | 'bekreft';

export class UtenlandsModal {
  private layer?: Phaser.GameObjects.Container;
  private unsubscribe?: () => void;
  private fase: Fase = 'velgBok';
  private valgtBokIsbn: string | null = null;
  private bekreftForlagId: string | null = null;
  private scrollY = 0;
  private scrollMax = 0;

  constructor(private scene: Phaser.Scene) {}

  open(): void {
    if (this.layer) return;
    this.layer = this.scene.add.container(0, 0).setDepth(120);

    const backdrop = this.scene.add
      .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.55)
      .setOrigin(0, 0)
      .setInteractive();
    this.layer.add(backdrop);

    this.scene.input.on('wheel', this.onWheel, this);
    this.fase = 'velgBok';
    this.valgtBokIsbn = null;
    this.bekreftForlagId = null;
    this.scrollY = 0;
    this.render();
    animateModalGlide(this.scene, this.layer);
    this.unsubscribe = gameState.subscribe(() => this.render());
  }

  close(): void {
    this.unsubscribe?.();
    this.unsubscribe = undefined;
    this.scene.input.off('wheel', this.onWheel, this);
    this.fase = 'velgBok';
    this.valgtBokIsbn = null;
    this.bekreftForlagId = null;
    this.scrollY = 0;
    this.layer?.destroy(true);
    this.layer = undefined;
  }

  get isOpen(): boolean {
    return !!this.layer;
  }

  private render(): void {
    if (!this.layer) return;
    while (this.layer.list.length > 1) {
      const last = this.layer.list[this.layer.list.length - 1];
      this.layer.remove(last, true);
    }

    const snap = gameState.snapshot;
    const sheetX = (GAME_WIDTH - SHEET_WIDTH) / 2;
    const sheetY = 96;
    const sheetH = GAME_HEIGHT - 192;

    const sheet = this.scene.add
      .rectangle(sheetX, sheetY, SHEET_WIDTH, sheetH, COLORS.beige, 0.98)
      .setOrigin(0, 0)
      .setStrokeStyle(3, COLORS.wine, 0.6);
    this.layer.add(sheet);

    const topBorder = this.scene.add
      .rectangle(sheetX, sheetY, SHEET_WIDTH, 4, COLORS.blueSoft, 1)
      .setOrigin(0, 0);
    this.layer.add(topBorder);

    const headerY = sheetY + 36;
    const header = this.scene.add
      .text(sheetX + ROW_PADDING, headerY, 'Internasjonalt salg', {
        fontFamily: FONTS.display,
        fontSize: fontStr(32),
        color: '#862736',
        wordWrap: { width: SHEET_WIDTH - ROW_PADDING * 2 - 110 },
      })
      .setOrigin(0, 0);
    this.layer.add(header);

    const beskrivelse = this.scene.add
      .text(
        sheetX + ROW_PADDING,
        headerY + 76,
        `Selg oversettelses-rettigheter til utlandet. Trenger bok med minst ${UTENLANDS_MIN_SALG_GLOBAL.toLocaleString('nb-NO')} salg og snitt ${UTENLANDS_MIN_SNITT_GLOBAL.toFixed(1)}/6.`,
        {
          fontFamily: FONTS.body,
          fontSize: fontStr(20),
          color: '#5a4a3a',
          wordWrap: { width: SHEET_WIDTH - ROW_PADDING * 2 - 110 },
        },
      )
      .setOrigin(0, 0);
    this.layer.add(beskrivelse);

    const kapital = this.scene.add
      .text(
        sheetX + ROW_PADDING,
        headerY + 160,
        `Dag ${snap.dag} · ${snap.kroner.toLocaleString('nb-NO')} kr`,
        {
          fontFamily: FONTS.hud,
          fontSize: fontStr(18),
          color: '#5a4a3a',
          fontStyle: '600',
        },
      )
      .setOrigin(0, 0);
    this.layer.add(kapital);

    const closeBtn = this.scene.add
      .text(sheetX + SHEET_WIDTH - ROW_PADDING, headerY - 28, '×', {
        fontFamily: FONTS.display,
        fontSize: fontStr(72),
        color: '#1a1a1a',
      })
      .setOrigin(1, 0)
      .setInteractive({ useHandCursor: true });
    closeBtn.on('pointerup', () => this.close());
    this.layer.add(closeBtn);

    if (this.fase === 'velgForlag' || this.fase === 'bekreft') {
      const tilbake = this.scene.add
        .text(sheetX + ROW_PADDING, headerY + 200, '‹ tilbake til bok-valg', {
          fontFamily: FONTS.hud,
          fontSize: fontStr(18),
          color: '#862736',
          fontStyle: '600',
        })
        .setOrigin(0, 0)
        .setInteractive({ useHandCursor: true });
      tilbake.on('pointerup', () => {
        this.fase = 'velgBok';
        this.valgtBokIsbn = null;
        this.bekreftForlagId = null;
        this.scrollY = 0;
        this.render();
      });
      this.layer.add(tilbake);
    }

    const listX = sheetX + (SHEET_WIDTH - ROW_WIDTH) / 2;
    const listTop = headerY + 240;
    const listBunn = sheetY + sheetH - 32;
    const listHoyde = listBunn - listTop;

    const listContainer = this.scene.add.container(0, 0);
    this.layer.add(listContainer);

    const maske = this.scene.make.graphics({ x: 0, y: 0 }, false);
    maske.fillStyle(0xffffff);
    maske.fillRect(listX - 4, listTop, ROW_WIDTH + 8, listHoyde);
    const maskeObj = maske.createGeometryMask();
    listContainer.setMask(maskeObj);

    if (this.fase === 'velgBok') {
      this.renderBokFase(listContainer, listX, listTop, listHoyde);
    } else {
      this.renderForlagFase(listContainer, listX, listTop, listHoyde);
    }

    this.bindScroll(listX, listTop, listHoyde);

    if (this.fase === 'bekreft') {
      this.renderBekreftModal();
    }
  }

  private renderBokFase(
    container: Phaser.GameObjects.Container,
    listX: number,
    listTop: number,
    listHoyde: number,
  ): void {
    const boker = gameState.utenlandsKvalifiserteBoker();
    boker.sort((a, b) => b.salg - a.salg);

    let y = listTop - this.scrollY;
    let bygdHoyde = 0;

    if (boker.length === 0) {
      this.tegnTomMelding(
        listX,
        listTop,
        'Ingen bøker er kvalifisert ennå.\nDu trenger en utgitt bok med minst ' +
          `${UTENLANDS_MIN_SALG_GLOBAL.toLocaleString('nb-NO')} salg og anmeldelses-snitt ${UTENLANDS_MIN_SNITT_GLOBAL.toFixed(1)}.`,
      );
      this.scrollMax = 0;
      return;
    }

    for (const bok of boker) {
      const rad = this.byggBokRad(listX, y, bok);
      container.add(rad);
      y += BOK_RAD_HOYDE + BOK_RAD_GAP;
      bygdHoyde += BOK_RAD_HOYDE + BOK_RAD_GAP;
    }

    this.scrollMax = Math.max(0, bygdHoyde - listHoyde);
    if (this.scrollY > this.scrollMax) this.scrollY = this.scrollMax;
  }

  private byggBokRad(
    x: number,
    y: number,
    bok: ContractedBook,
  ): Phaser.GameObjects.Container {
    const c = this.scene.add.container(x, y);

    const bg = this.scene.add
      .rectangle(0, 0, ROW_WIDTH, BOK_RAD_HOYDE, COLORS.white, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(2, COLORS.oakDark, 0.35);
    c.add(bg);

    const venstreBorder = this.scene.add
      .rectangle(0, 0, 4, BOK_RAD_HOYDE, COLORS.blueSoft, 1)
      .setOrigin(0, 0);
    c.add(venstreBorder);

    const tittel = this.scene.add
      .text(ROW_PADDING, 16, bok.title, {
        fontFamily: FONTS.display,
        fontSize: fontStr(24),
        color: '#1a1a1a',
        wordWrap: { width: ROW_WIDTH - 320 },
      })
      .setOrigin(0, 0);
    c.add(tittel);

    const forfatter = this.scene.add
      .text(ROW_PADDING, 68, `${bok.author} · ${bok.genre}`, {
        fontFamily: FONTS.body,
        fontSize: fontStr(18),
        color: '#5a4a3a',
        fontStyle: 'italic',
      })
      .setOrigin(0, 0);
    c.add(forfatter);

    const snitt = snittTerninger(bok);
    const eksisterende = gameState.internasjonaleAvtaler.filter(
      (a) => a.isbn === bok.isbn,
    ).length;
    const meta = this.scene.add
      .text(
        ROW_PADDING,
        108,
        `${bok.salg.toLocaleString('nb-NO')} solgt · snitt ${snitt.toFixed(1)}/6 · ${eksisterende} marked solgt`,
        {
          fontFamily: FONTS.hud,
          fontSize: fontStr(16),
          color: '#5a4a3a',
          wordWrap: { width: ROW_WIDTH - 320 },
        },
      )
      .setOrigin(0, 0);
    c.add(meta);

    const knappW = 260;
    const knappH = 66;
    const knappX = ROW_WIDTH - knappW - 16;
    const knappY = (BOK_RAD_HOYDE - knappH) / 2;

    const kBg = this.scene.add
      .rectangle(knappX, knappY, knappW, knappH, COLORS.wine, 1)
      .setOrigin(0, 0);
    const kTxt = this.scene.add
      .text(knappX + knappW / 2, knappY + knappH / 2, 'Se markeder ›', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(18),
        color: '#ffffff',
        fontStyle: '700',
      })
      .setOrigin(0.5);
    c.add([kBg, kTxt]);
    kBg.setInteractive({ useHandCursor: true });
    kBg.on('pointerover', () => kBg.setAlpha(0.85));
    kBg.on('pointerout', () => kBg.setAlpha(1));
    kBg.on('pointerup', () => {
      this.valgtBokIsbn = bok.isbn;
      this.fase = 'velgForlag';
      this.scrollY = 0;
      this.render();
    });

    return c;
  }

  private renderForlagFase(
    container: Phaser.GameObjects.Container,
    listX: number,
    listTop: number,
    listHoyde: number,
  ): void {
    const isbn = this.valgtBokIsbn;
    if (!isbn) {
      this.fase = 'velgBok';
      this.render();
      return;
    }
    const snap = gameState.snapshot;
    const bok =
      snap.utgitteBoker.find((b) => b.isbn === isbn) ??
      snap.iProduksjon.find((b) => b.isbn === isbn);
    if (!bok) {
      this.fase = 'velgBok';
      this.valgtBokIsbn = null;
      this.render();
      return;
    }

    const bokOverskrift = this.scene.add
      .text(listX, listTop - 36, `«${bok.title}» — velg marked`, {
        fontFamily: FONTS.display,
        fontSize: fontStr(22),
        color: '#862736',
        wordWrap: { width: ROW_WIDTH },
      })
      .setOrigin(0, 0);
    this.layer?.add(bokOverskrift);

    let y = listTop - this.scrollY;
    let bygdHoyde = 0;

    for (const forlag of UTENLANDSKE_FORLAG) {
      const rad = this.byggForlagRad(listX, y, bok, forlag);
      container.add(rad);
      y += FORLAG_RAD_HOYDE + FORLAG_RAD_GAP;
      bygdHoyde += FORLAG_RAD_HOYDE + FORLAG_RAD_GAP;
    }

    this.scrollMax = Math.max(0, bygdHoyde - listHoyde);
    if (this.scrollY > this.scrollMax) this.scrollY = this.scrollMax;
  }

  private byggForlagRad(
    x: number,
    y: number,
    bok: ContractedBook,
    forlag: UtenlandskForlag,
  ): Phaser.GameObjects.Container {
    const c = this.scene.add.container(x, y);

    const allerede = gameState.harUtenlandsAvtale(bok.isbn, forlag.id);
    const tilbud = beregnUtenlandsTilbud(bok, forlag, gameState.tier);
    const passer = tilbud.passer && !allerede;
    const borderFarge = passer ? 0xb8860b : 0x8b8b8b;
    const bgAlpha = passer ? 1 : 0.78;

    const bg = this.scene.add
      .rectangle(0, 0, ROW_WIDTH, FORLAG_RAD_HOYDE, COLORS.white, bgAlpha)
      .setOrigin(0, 0)
      .setStrokeStyle(2, COLORS.oakDark, 0.3);
    c.add(bg);

    const venstreBorder = this.scene.add
      .rectangle(0, 0, 6, FORLAG_RAD_HOYDE, borderFarge, 1)
      .setOrigin(0, 0);
    c.add(venstreBorder);

    const flagg = this.scene.add
      .text(ROW_PADDING, 18, forlag.flagg, {
        fontFamily: FONTS.display,
        fontSize: fontStr(32),
        color: '#1a1a1a',
      })
      .setOrigin(0, 0);
    c.add(flagg);

    const navn = this.scene.add
      .text(ROW_PADDING + 70, 16, `${forlag.navn}`, {
        fontFamily: FONTS.display,
        fontSize: fontStr(24),
        color: '#862736',
        wordWrap: { width: ROW_WIDTH - 70 - ROW_PADDING - 280 },
      })
      .setOrigin(0, 0);
    c.add(navn);

    const landSprak = this.scene.add
      .text(ROW_PADDING + 70, 60, `${forlag.land} · ${forlag.sprak}`, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(15),
        color: '#5a4a3a',
        fontStyle: '600',
      })
      .setOrigin(0, 0);
    c.add(landSprak);

    const arketype = this.scene.add
      .text(ROW_PADDING, 102, forlag.arketype, {
        fontFamily: FONTS.body,
        fontSize: fontStr(15),
        color: '#5a4a3a',
        fontStyle: 'italic',
        wordWrap: { width: ROW_WIDTH - 320 },
      })
      .setOrigin(0, 0);
    c.add(arketype);

    const vurdering = this.byggForlagVurdering(bok, forlag, allerede, tilbud.passer);
    const vurderingT = this.scene.add
      .text(ROW_PADDING, FORLAG_RAD_HOYDE - 16, vurdering, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(14),
        color: passer ? '#8b5e3c' : '#7a6f5e',
        fontStyle: '700',
      })
      .setOrigin(0, 1);
    c.add(vurderingT);

    const knappW = 260;
    const knappH = 72;
    const knappX = ROW_WIDTH - knappW - 16;
    const knappY = (FORLAG_RAD_HOYDE - knappH) / 2;

    if (allerede) {
      const tagBg = this.scene.add
        .rectangle(knappX, knappY, knappW, knappH, COLORS.forest, 1)
        .setOrigin(0, 0);
      const tagTxt = this.scene.add
        .text(knappX + knappW / 2, knappY + knappH / 2, 'Allerede solgt ✓', {
          fontFamily: FONTS.hud,
          fontSize: fontStr(17),
          color: '#ffffff',
          fontStyle: '700',
        })
        .setOrigin(0.5);
      c.add([tagBg, tagTxt]);
    } else if (passer) {
      const fill = COLORS.wine;
      const tekst = `Selg — ${tilbud.engangs.toLocaleString('nb-NO')} kr`;
      const royaltyTekst = `+${tilbud.royalty} kr/salg`;
      const kBg = this.scene.add
        .rectangle(knappX, knappY, knappW, knappH, fill, 1)
        .setOrigin(0, 0);
      const kTxt = this.scene.add
        .text(knappX + knappW / 2, knappY + knappH / 2 - 8, tekst, {
          fontFamily: FONTS.hud,
          fontSize: fontStr(16),
          color: '#ffffff',
          fontStyle: '700',
        })
        .setOrigin(0.5);
      const kRoyalty = this.scene.add
        .text(knappX + knappW / 2, knappY + knappH / 2 + 14, royaltyTekst, {
          fontFamily: FONTS.hud,
          fontSize: fontStr(13),
          color: '#f4d58d',
        })
        .setOrigin(0.5);
      c.add([kBg, kTxt, kRoyalty]);
      kBg.setInteractive({ useHandCursor: true });
      kBg.on('pointerover', () => kBg.setAlpha(0.85));
      kBg.on('pointerout', () => kBg.setAlpha(1));
      kBg.on('pointerup', () => {
        this.bekreftForlagId = forlag.id;
        this.fase = 'bekreft';
        this.render();
      });
    } else {
      const tagBg = this.scene.add
        .rectangle(knappX, knappY, knappW, knappH, 0xcfc6b7, 1)
        .setOrigin(0, 0);
      const tagTxt = this.scene.add
        .text(knappX + knappW / 2, knappY + knappH / 2, 'Passer ikke', {
          fontFamily: FONTS.hud,
          fontSize: fontStr(17),
          color: '#7a7060',
          fontStyle: '700',
        })
        .setOrigin(0.5);
      c.add([tagBg, tagTxt]);
    }

    return c;
  }

  private byggForlagVurdering(
    bok: ContractedBook,
    forlag: UtenlandskForlag,
    allerede: boolean,
    passer: boolean,
  ): string {
    if (allerede) return 'Allerede solgt til dette markedet';
    if (passer) return `Passer — vil ha ${forlag.sprak}-rettigheter`;
    const snitt = snittTerninger(bok);
    if (bok.salg < forlag.minSalg) {
      return `Krever ${forlag.minSalg.toLocaleString('nb-NO')} salg (du har ${bok.salg.toLocaleString('nb-NO')})`;
    }
    if (snitt < forlag.minAnmeldelsesSnitt) {
      return `Krever snitt ${forlag.minAnmeldelsesSnitt.toFixed(1)} (du har ${snitt.toFixed(1)})`;
    }
    return 'Passer ikke akkurat nå';
  }

  private renderBekreftModal(): void {
    if (!this.layer) return;
    const isbn = this.valgtBokIsbn;
    const forlagId = this.bekreftForlagId;
    if (!isbn || !forlagId) return;

    const snap = gameState.snapshot;
    const bok =
      snap.utgitteBoker.find((b) => b.isbn === isbn) ??
      snap.iProduksjon.find((b) => b.isbn === isbn);
    if (!bok) return;
    const forlag = UTENLANDSKE_FORLAG.find((f) => f.id === forlagId);
    if (!forlag) return;
    const tilbud = beregnUtenlandsTilbud(bok, forlag, snap.tier);
    if (!tilbud.passer) return;

    const overlay = this.scene.add
      .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.55)
      .setOrigin(0, 0)
      .setInteractive();
    this.layer.add(overlay);

    const modalW = 840;
    const modalH = 480;
    const modalX = (GAME_WIDTH - modalW) / 2;
    const modalY = (GAME_HEIGHT - modalH) / 2;

    const modal = this.scene.add
      .rectangle(modalX, modalY, modalW, modalH, COLORS.beige, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(3, COLORS.wine, 0.8);
    this.layer.add(modal);

    const tittel = this.scene.add
      .text(
        modalX + modalW / 2,
        modalY + 40,
        `${forlag.flagg}  ${forlag.navn}`,
        {
          fontFamily: FONTS.display,
          fontSize: fontStr(32),
          color: '#862736',
          align: 'center',
        },
      )
      .setOrigin(0.5, 0);
    this.layer.add(tittel);

    const tekst = `Selg «${bok.title}» til ${forlag.navn} for ${tilbud.engangs.toLocaleString('nb-NO')} kr engangs-betaling + ${tilbud.royalty} kr royalty per fremtidig salg?`;
    const beskrivelse = this.scene.add
      .text(modalX + 32, modalY + 110, tekst, {
        fontFamily: FONTS.body,
        fontSize: fontStr(20),
        color: '#1a1a1a',
        wordWrap: { width: modalW - 64 },
        align: 'left',
      })
      .setOrigin(0, 0);
    this.layer.add(beskrivelse);

    const knappW = 280;
    const knappH = 64;
    const knappGap = 24;
    const knappY = modalY + modalH - knappH - 32;
    const totalBredde = knappW * 2 + knappGap;
    const venstreX = modalX + (modalW - totalBredde) / 2;

    const avbrytBg = this.scene.add
      .rectangle(venstreX, knappY, knappW, knappH, COLORS.beigeDark, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(2, COLORS.oakDark, 0.5);
    const avbrytTxt = this.scene.add
      .text(venstreX + knappW / 2, knappY + knappH / 2, 'Avbryt', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(20),
        color: '#1a1a1a',
        fontStyle: '700',
      })
      .setOrigin(0.5);
    this.layer.add([avbrytBg, avbrytTxt]);
    avbrytBg.setInteractive({ useHandCursor: true });
    avbrytBg.on('pointerup', () => {
      this.bekreftForlagId = null;
      this.fase = 'velgForlag';
      this.render();
    });

    const selgX = venstreX + knappW + knappGap;
    const selgBg = this.scene.add
      .rectangle(selgX, knappY, knappW, knappH, COLORS.wine, 1)
      .setOrigin(0, 0);
    const selgTxt = this.scene.add
      .text(selgX + knappW / 2, knappY + knappH / 2, 'Ja, selg', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(20),
        color: '#ffffff',
        fontStyle: '700',
      })
      .setOrigin(0.5);
    this.layer.add([selgBg, selgTxt]);
    selgBg.setInteractive({ useHandCursor: true });
    selgBg.on('pointerover', () => selgBg.setAlpha(0.85));
    selgBg.on('pointerout', () => selgBg.setAlpha(1));
    selgBg.on('pointerup', () => {
      const ok = gameState.selgTilUtland(bok.isbn, forlag.id);
      if (ok) {
        this.bekreftForlagId = null;
        this.fase = 'velgForlag';
        this.render();
      }
    });
  }

  private tegnTomMelding(x: number, y: number, melding: string): void {
    if (!this.layer) return;
    const tomBoks = this.scene.add
      .rectangle(x, y, ROW_WIDTH, 180, COLORS.white, 0.9)
      .setOrigin(0, 0)
      .setStrokeStyle(2, COLORS.oakDark, 0.4);
    const tomTxt = this.scene.add
      .text(x + ROW_WIDTH / 2, y + 90, melding, {
        fontFamily: FONTS.body,
        fontSize: fontStr(22),
        color: '#1a1a1a',
        align: 'center',
        wordWrap: { width: ROW_WIDTH - 64 },
      })
      .setOrigin(0.5);
    this.layer.add([tomBoks, tomTxt]);
  }

  private bindScroll(listX: number, listTop: number, listHoyde: number): void {
    if (!this.layer) return;
    if (this.scrollMax <= 0) return;
    const sone = this.scene.add
      .rectangle(listX, listTop, ROW_WIDTH, listHoyde, 0x000000, 0)
      .setOrigin(0, 0)
      .setInteractive();
    let dragStart = 0;
    let scrollStart = 0;
    sone.on('pointerdown', (p: Phaser.Input.Pointer) => {
      dragStart = p.y;
      scrollStart = this.scrollY;
    });
    sone.on('pointermove', (p: Phaser.Input.Pointer) => {
      if (!p.isDown) return;
      const delta = dragStart - p.y;
      this.scrollY = Math.max(0, Math.min(this.scrollMax, scrollStart + delta));
      this.render();
    });
    this.layer.add(sone);
  }

  private onWheel = (
    _pointer: Phaser.Input.Pointer,
    _objs: Phaser.GameObjects.GameObject[],
    _dx: number,
    dy: number,
  ): void => {
    if (!this.layer || this.scrollMax <= 0) return;
    this.scrollY = Math.max(0, Math.min(this.scrollMax, this.scrollY + dy));
    this.render();
  };
}
